// Página ProductPriceHistory - Historial de cambios de precio de un producto
// Muestra el historial completo de cambios de precio y permite revertir cambios

import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { productsService } from '../../services/firestore.service'
import { useProduct } from '../../hooks/useFirestore'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatDate } from '../../utils/helpers'
import Button from '../../components/Button'
import ConfirmDialog from '../../components/ConfirmDialog'
import Toast from '../../components/Toast'
import './ProductPriceHistory.css'

/**
 * Página ProductPriceHistory
 * Muestra el historial de cambios de precio de un producto
 */
function ProductPriceHistory() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { currentUser, userData } = useAuth()
  const { product, loading: loadingProduct } = useProduct(id)
  
  const [priceHistory, setPriceHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [notification, setNotification] = useState(null)
  const [showRevertDialog, setShowRevertDialog] = useState(false)
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(null)

  useEffect(() => {
    if (product) {
      setPriceHistory(product.priceHistory || [])
      setLoading(false)
    }
  }, [product])

  const handleRevert = async (historyIndex) => {
    setSelectedHistoryIndex(historyIndex)
    setShowRevertDialog(true)
  }

  const confirmRevert = async () => {
    if (selectedHistoryIndex === null) return

    try {
      await productsService.revertPrice(
        id,
        selectedHistoryIndex,
        {
          userId: currentUser?.uid || null,
          userName: userData?.name || userData?.displayName || 'Sistema',
          notes: `Reversión manual desde historial de precios`
        }
      )

      // Recargar el producto para obtener el historial actualizado
      window.location.reload()
      
      setNotification({
        type: 'success',
        message: 'Precio revertido correctamente'
      })
      setTimeout(() => setNotification(null), 3000)
    } catch (error) {
      console.error('Error revirtiendo precio:', error)
      setNotification({
        type: 'error',
        message: `Error al revertir precio: ${error.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 5000)
    } finally {
      setShowRevertDialog(false)
      setSelectedHistoryIndex(null)
    }
  }

  const handleExportCSV = () => {
    if (!product || priceHistory.length === 0) {
      setNotification({
        type: 'error',
        message: 'No hay historial para exportar'
      })
      setTimeout(() => setNotification(null), 3000)
      return
    }

    // Crear contenido CSV
    const headers = ['Fecha', 'Precio Anterior', 'Precio Nuevo', 'Cambio', 'Cambio %', 'Usuario', 'Razón', 'Orden de Compra', 'Notas']
    const rows = priceHistory.map(entry => {
      const change = entry.newPrice - entry.previousPrice
      const changePercent = entry.previousPrice > 0 
        ? ((change / entry.previousPrice) * 100).toFixed(2)
        : '0.00'
      
      return [
        formatDate(entry.changedAt),
        formatCurrency(entry.previousPrice),
        formatCurrency(entry.newPrice),
        formatCurrency(change),
        `${changePercent}%`,
        entry.changedByName || entry.changedBy || 'N/A',
        entry.reason || 'N/A',
        entry.purchaseOrderId || 'N/A',
        entry.notes || 'N/A'
      ]
    })

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    // Crear blob y descargar
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `historial_precios_${product.name || product.id}_${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    setNotification({
      type: 'success',
      message: 'Historial exportado correctamente'
    })
    setTimeout(() => setNotification(null), 3000)
  }

  if (loadingProduct || loading) {
    return (
      <div className="product-price-history-page">
        <div className="loading-state">
          <p>Cargando historial...</p>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="product-price-history-page">
        <div className="error-state">
          <p>Producto no encontrado</p>
          <Button onClick={() => navigate('/admin/productos')}>
            Volver a Productos
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="product-price-history-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />

      <div className="history-header">
        <div className="header-left">
          <Link to={`/admin/productos/${id}`} className="back-link">
            ← Volver al Producto
          </Link>
          <h1>Historial de Precios</h1>
          <p className="product-name">{product.name}</p>
          <p className="product-sku">SKU: {product.sku || 'N/A'}</p>
        </div>
        <div className="header-right">
          <div className="current-price-info">
            <span className="label">Precio Actual:</span>
            <span className="value">{formatCurrency(product.price || 0)}</span>
          </div>
          {priceHistory.length > 0 && (
            <Button onClick={handleExportCSV} variant="outline">
              📥 Exportar CSV
            </Button>
          )}
        </div>
      </div>

      {priceHistory.length === 0 ? (
        <div className="empty-history">
          <p>No hay historial de cambios de precio para este producto.</p>
          <p className="hint">El historial se creará automáticamente cuando se actualice el precio desde una orden de compra o manualmente.</p>
        </div>
      ) : (
        <div className="history-content">
          <div className="history-stats">
            <div className="stat-card">
              <span className="stat-label">Total de Cambios</span>
              <span className="stat-value">{priceHistory.length}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Primer Precio</span>
              <span className="stat-value">{formatCurrency(priceHistory[priceHistory.length - 1]?.previousPrice || 0)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Precio Actual</span>
              <span className="stat-value">{formatCurrency(product.price || 0)}</span>
            </div>
            <div className="stat-card">
              <span className="stat-label">Cambio Total</span>
              <span className={`stat-value ${(product.price || 0) > (priceHistory[priceHistory.length - 1]?.previousPrice || 0) ? 'positive' : 'negative'}`}>
                {formatCurrency((product.price || 0) - (priceHistory[priceHistory.length - 1]?.previousPrice || 0))}
              </span>
            </div>
          </div>

          <div className="history-table-container">
            <table className="history-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Precio Anterior</th>
                  <th>Precio Nuevo</th>
                  <th>Cambio</th>
                  <th>Cambio %</th>
                  <th>Usuario</th>
                  <th>Razón</th>
                  <th>Orden de Compra</th>
                  <th>Notas</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {priceHistory.map((entry, index) => {
                  const change = entry.newPrice - entry.previousPrice
                  const changePercent = entry.previousPrice > 0 
                    ? ((change / entry.previousPrice) * 100).toFixed(2)
                    : '0.00'
                  const isSignificant = Math.abs(parseFloat(changePercent)) > 20
                  const isLatest = index === priceHistory.length - 1
                  
                  return (
                    <tr 
                      key={index} 
                      className={`${isLatest ? 'latest-entry' : ''} ${isSignificant ? 'significant-change' : ''}`}
                    >
                      <td>{formatDate(entry.changedAt)}</td>
                      <td className="previous-price">{formatCurrency(entry.previousPrice)}</td>
                      <td className={`new-price ${change >= 0 ? 'increase' : 'decrease'}`}>
                        {formatCurrency(entry.newPrice)}
                      </td>
                      <td className={`change ${change >= 0 ? 'positive' : 'negative'}`}>
                        {change >= 0 ? '+' : ''}{formatCurrency(change)}
                      </td>
                      <td className={`change-percent ${change >= 0 ? 'positive' : 'negative'}`}>
                        {change >= 0 ? '+' : ''}{changePercent}%
                        {isSignificant && <span className="warning-icon">⚠️</span>}
                      </td>
                      <td>{entry.changedByName || entry.changedBy || 'N/A'}</td>
                      <td>{entry.reason || 'N/A'}</td>
                      <td>
                        {entry.purchaseOrderId ? (
                          <Link 
                            to={`/admin/pedidos-compra/${entry.purchaseOrderId}`}
                            className="order-link"
                          >
                            Ver Orden
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td className="notes-cell">{entry.notes || 'N/A'}</td>
                      <td>
                        {!isLatest && (
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => handleRevert(index)}
                          >
                            ↶ Revertir
                          </Button>
                        )}
                        {isLatest && <span className="current-badge">Actual</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={showRevertDialog}
        onClose={() => {
          setShowRevertDialog(false)
          setSelectedHistoryIndex(null)
        }}
        onConfirm={confirmRevert}
        title="Confirmar Reversión de Precio"
        message={
          selectedHistoryIndex !== null && priceHistory[selectedHistoryIndex] ? (
            <div className="revert-confirm-content">
              <p>¿Estás seguro de que deseas revertir el precio a:</p>
              <div className="revert-price-info">
                <div className="price-comparison">
                  <div className="price-item">
                    <span className="label">Precio Actual:</span>
                    <span className="value">{formatCurrency(product.price || 0)}</span>
                  </div>
                  <div className="price-arrow">→</div>
                  <div className="price-item">
                    <span className="label">Precio Anterior:</span>
                    <span className="value revert-value">
                      {formatCurrency(priceHistory[selectedHistoryIndex].previousPrice)}
                    </span>
                  </div>
                </div>
                <p className="revert-warning">
                  ⚠️ Esta acción creará una nueva entrada en el historial y actualizará el precio del producto y todas sus variantes.
                </p>
              </div>
            </div>
          ) : (
            '¿Estás seguro de que deseas revertir este precio?'
          )
        }
        confirmText="Confirmar Reversión"
        cancelText="Cancelar"
        variant="warning"
      />
    </div>
  )
}

export default ProductPriceHistory

